/**
 * Image Upload Component
 * Drag-and-drop image upload interface with validation, preview, and gallery
 */

import { useState, useRef, useCallback } from 'react';
import { Upload, X, Image as ImageIcon, AlertCircle } from 'lucide-react';
import { cn } from '../lib/utils';

export interface UploadedImage {
  id: string;
  file: File;
  preview: string;
  name: string;
  size: number;
  status: 'uploading' | 'success' | 'error';
  progress?: number;
  error?: string;
}

export interface ImageUploadProps {
  maxFiles?: number;
  maxFileSize?: number; // in bytes
  acceptedFormats?: string[];
  onUpload?: (files: File[]) => Promise<void>;
  onRemove?: (imageId: string) => void;
  disabled?: boolean;
  className?: string;
  images?: UploadedImage[];
}

const DEFAULT_MAX_FILES = 5;
const DEFAULT_MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const DEFAULT_ACCEPTED_FORMATS = ['image/jpeg', 'image/png', 'image/webp'];

export default function ImageUpload({
  maxFiles = DEFAULT_MAX_FILES,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  acceptedFormats = DEFAULT_ACCEPTED_FORMATS,
  onUpload,
  onRemove,
  disabled = false,
  className,
  images = []
}: ImageUploadProps) {
  const [dragActive, setDragActive] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  const validateFile = (file: File): string | null => {
    if (!acceptedFormats.includes(file.type)) {
      return `Invalid file format. Accepted formats: ${acceptedFormats.map(f => f.split('/')[1].toUpperCase()).join(', ')}`;
    }
    
    if (file.size > maxFileSize) {
      return `File size too large. Maximum size: ${formatFileSize(maxFileSize)}`;
    }
    
    return null;
  };

  const validateFiles = (files: File[]): { validFiles: File[]; errors: string[] } => {
    const errors: string[] = [];
    const validFiles: File[] = [];

    // Check total file count
    if (images.length + files.length > maxFiles) {
      errors.push(`Maximum ${maxFiles} images allowed. You can upload ${maxFiles - images.length} more.`);
      return { validFiles: [], errors };
    }

    files.forEach((file, index) => {
      const error = validateFile(file);
      if (error) {
        errors.push(`File ${index + 1} (${file.name}): ${error}`);
      } else {
        validFiles.push(file);
      }
    });

    return { validFiles, errors };
  };

  const handleFiles = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const { validFiles, errors } = validateFiles(fileArray);
    
    setUploadErrors(errors);

    if (validFiles.length > 0 && onUpload) {
      try {
        await onUpload(validFiles);
      } catch (error) {
        console.error('Upload failed:', error);
        setUploadErrors(prev => [...prev, 'Upload failed. Please try again.']);
      }
    }
  }, [images.length, maxFiles, onUpload]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragActive(true);
    }
  }, []);

  const handleDragOut = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    dragCounter.current = 0;
    
    if (disabled) return;
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, [disabled, handleFiles]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
      // Reset input value to allow selecting the same file again
      e.target.value = '';
    }
  }, [handleFiles]);

  const openFileDialog = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRemove = (imageId: string) => {
    if (onRemove) {
      onRemove(imageId);
    }
    // Clear any errors when removing files
    setUploadErrors([]);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const canUploadMore = images.length < maxFiles;

  return (
    <div className={cn('space-y-4', className)}>
      {/* Upload Area */}
      {canUploadMore && (
        <div
          className={cn(
            'relative border-2 border-dashed rounded-lg p-6 transition-colors',
            dragActive
              ? 'border-blue-400 bg-blue-50'
              : 'border-gray-300 hover:border-gray-400',
            disabled && 'opacity-50 cursor-not-allowed',
            !disabled && 'cursor-pointer'
          )}
          onDragEnter={handleDragIn}
          onDragLeave={handleDragOut}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={openFileDialog}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={acceptedFormats.join(',')}
            onChange={handleFileInput}
            className="hidden"
            disabled={disabled}
          />
          
          <div className="text-center">
            <Upload className={cn(
              'mx-auto h-12 w-12 mb-4',
              dragActive ? 'text-blue-500' : 'text-gray-400'
            )} />
            
            <div className="space-y-2">
              <p className="text-lg font-medium text-gray-900">
                {dragActive ? 'Drop images here' : 'Upload product images'}
              </p>
              <p className="text-sm text-gray-500">
                Drag and drop images here, or click to select files
              </p>
              <p className="text-xs text-gray-400">
                {acceptedFormats.map(f => f.split('/')[1].toUpperCase()).join(', ')} • 
                Max {formatFileSize(maxFileSize)} per file • 
                Up to {maxFiles - images.length} more images
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Upload Errors */}
      {uploadErrors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-red-800 mb-2">Upload Errors</h4>
              <ul className="text-sm text-red-700 space-y-1">
                {uploadErrors.map((error, index) => (
                  <li key={index}>• {error}</li>
                ))}
              </ul>
            </div>
            <button
              onClick={() => setUploadErrors([])}
              className="text-red-400 hover:text-red-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Image Gallery */}
      {images.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-900">
              Uploaded Images ({images.length}/{maxFiles})
            </h4>
            {images.length > 1 && (
              <p className="text-xs text-gray-500">
                Drag to reorder
              </p>
            )}
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {images.map((image, index) => (
              <ImagePreview
                key={image.id}
                image={image}
                index={index}
                onRemove={handleRemove}
                disabled={disabled}
              />
            ))}
          </div>
        </div>
      )}

      {/* Upload Limit Info */}
      {images.length >= maxFiles && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
            <p className="text-sm text-amber-800">
              Maximum number of images reached ({maxFiles}). Remove an image to upload a new one.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

interface ImagePreviewProps {
  image: UploadedImage;
  index: number;
  onRemove: (imageId: string) => void;
  disabled: boolean;
}

function ImagePreview({ image, index, onRemove, disabled }: ImagePreviewProps) {
  const [imageError, setImageError] = useState(false);

  return (
    <div className="relative group bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      {/* Image */}
      <div className="aspect-square bg-gray-100 flex items-center justify-center">
        {!imageError ? (
          <img
            src={image.preview}
            alt={image.name}
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="flex flex-col items-center justify-center text-gray-400">
            <ImageIcon className="w-8 h-8 mb-2" />
            <p className="text-xs text-center">Preview unavailable</p>
          </div>
        )}
      </div>

      {/* Overlay */}
      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200" />

      {/* Controls */}
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => onRemove(image.id)}
          disabled={disabled}
          className="bg-red-500 hover:bg-red-600 text-white rounded-full p-1 shadow-lg transition-colors disabled:opacity-50"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Status Indicator */}
      {image.status === 'uploading' && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="text-white text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mx-auto mb-2"></div>
            <p className="text-xs">Uploading...</p>
            {image.progress !== undefined && (
              <p className="text-xs mt-1">{Math.round(image.progress)}%</p>
            )}
          </div>
        </div>
      )}

      {image.status === 'error' && (
        <div className="absolute inset-0 bg-red-500 bg-opacity-80 flex items-center justify-center">
          <div className="text-white text-center">
            <AlertCircle className="w-6 h-6 mx-auto mb-2" />
            <p className="text-xs">Upload failed</p>
            {image.error && (
              <p className="text-xs mt-1 opacity-80">{image.error}</p>
            )}
          </div>
        </div>
      )}

      {/* Image Info */}
      <div className="p-2 bg-white">
        <p className="text-xs font-medium text-gray-900 truncate" title={image.name}>
          {image.name}
        </p>
        <div className="flex items-center justify-between mt-1">
          <p className="text-xs text-gray-500">
            {formatFileSize(image.size)}
          </p>
          <p className="text-xs text-gray-400">
            #{index + 1}
          </p>
        </div>
      </div>
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}